import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { LANGUAGES } from "../../i18n";

/**
 * LanguageSelector — grid of the five launch languages showing each language's
 * NATIVE name (so a user who can't read the current UI language still finds
 * theirs). Used in signup (step 0) and Settings → Language.
 *
 * Props:
 *   value       — selected language code
 *   onChange    — (code) => void
 *   suggested   — optional code auto-detected from device (shows a hint badge)
 */
export default function LanguageSelector({ value, onChange, suggested }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 gap-2" data-testid="language-selector">
      {LANGUAGES.map((l) => {
        const active = value === l.code;
        return (
          <button
            type="button"
            key={l.code}
            onClick={() => onChange(l.code)}
            data-testid={`lang-option-${l.code}`}
            aria-pressed={active}
            className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl border text-start transition ${
              active
                ? "border-[#E60000] bg-[#E60000]/10 shadow-[0_0_16px_rgba(230,0,0,0.25)]"
                : "border-white/10 hover:border-white/25"
            }`}
          >
            <span className="flex-1">
              <span className="block text-white font-semibold leading-tight">{l.native}</span>
              <span className="block text-xs text-zinc-500">{t(l.labelKey)}</span>
            </span>
            {suggested === l.code && !active && (
              <span className="text-[10px] font-mono-accent text-[#E60000] tracking-wider" data-testid={`lang-suggested-${l.code}`}>
                {t("language.autoDetected")}
              </span>
            )}
            {active && (
              <span className="w-6 h-6 rounded-full bg-[#E60000] flex items-center justify-center shrink-0">
                <Check size={14} className="text-white" strokeWidth={3} />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
